/**
 * Iter 185 (Feb 2026) — Document Management UI (Phases 1-3).
 *
 * `<DocumentsPanel>` is the reusable list/upload component used in
 * two places:
 *   • Lead Focus → Documents tab (scoped to a single lead_id)
 *   • Standalone /documents page (global tenant-wide view)
 *
 * Native click-to-sign opens a modal with a typed-name input + a
 * <canvas> drawing pad. Saving sends { typed_name, signature_data_url,
 * title, agreement_text } to POST /api/documents/{id}/sign — the
 * existing modal stays compatible when we add DocuSign later (same
 * surface, different backend implementation).
 */
import { useEffect, useRef, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Upload, Download, Trash2, FileText, PenTool, Filter, X, Eye, RefreshCw, History } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

const CATEGORIES = [
  "contract", "quote", "proposal", "baa", "phi", "compliance",
  "marketing", "presentation", "screenshot", "audio", "other",
];

const COMPLIANCE = new Set(["baa", "phi", "compliance"]);

const fmt = (n) => {
  if (n == null) return "—";
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / 1024 / 1024).toFixed(1)} MB`;
};

export function DocumentsPanel({ leadId = null, embedded = false }) {
  const token = localStorage.getItem("token") || "";
  const headers = { Authorization: `Bearer ${token}` };
  const [docs, setDocs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [category, setCategory] = useState("");
  const [search, setSearch] = useState("");
  const [signOpen, setSignOpen] = useState(null);
  const [previewOpen, setPreviewOpen] = useState(null);
  const [versionsOpen, setVersionsOpen] = useState(null);
  const replaceRef = useRef(null);
  const [replaceTarget, setReplaceTarget] = useState(null);

  const fetchDocs = async () => {
    setLoading(true);
    try {
      const params = { limit: 200 };
      if (leadId) params.lead_id = leadId;
      if (category) params.category = category;
      if (search) params.q = search;
      const r = await axios.get(`${API}/api/documents`, { headers, params });
      setDocs(r.data || []);
    } catch (e) {
      toast.error(`Failed to load documents: ${e?.response?.data?.detail || e.message}`);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchDocs(); /* eslint-disable-next-line */ }, [leadId, category, search]);

  const onUpload = async (files, uploadCategory) => {
    for (const f of Array.from(files || [])) {
      if (f.size > 50 * 1024 * 1024) {
        toast.error(`${f.name} exceeds the 50 MB limit`);
        continue;
      }
      const fd = new FormData();
      fd.append("file", f);
      fd.append("category", uploadCategory || "other");
      if (leadId) fd.append("lead_id", leadId);
      try {
        await axios.post(`${API}/api/documents/upload`, fd, { headers });
        toast.success(`Uploaded ${f.name}`);
      } catch (e) {
        toast.error(`Upload failed: ${e?.response?.data?.detail || e.message}`);
      }
    }
    fetchDocs();
  };

  const onDownload = async (doc) => {
    try {
      const r = await axios.get(`${API}/api/documents/${doc.id}/download`, { headers });
      // Local FS emulator returns a relative URL (/api/documents/_blob/...);
      // Azure Blob returns an absolute https://...blob.core.windows.net URL.
      const signed = r.data.url || "";
      const url = /^https?:\/\//i.test(signed) ? signed : `${API}${signed}`;
      const a = document.createElement("a");
      a.href = url; a.download = doc.filename; a.target = "_blank";
      document.body.appendChild(a); a.click(); a.remove();
    } catch (e) {
      toast.error(`Download failed: ${e?.response?.data?.detail || e.message}`);
    }
  };

  const onDelete = async (doc) => {
    if (!window.confirm(`Delete "${doc.filename}"? This is a soft delete — admins can recover.`)) return;
    try {
      await axios.delete(`${API}/api/documents/${doc.id}`, { headers });
      toast.success("Deleted");
      fetchDocs();
    } catch (e) {
      toast.error(`Delete failed: ${e?.response?.data?.detail || e.message}`);
    }
  };

  const onReplaceClick = (doc) => {
    setReplaceTarget(doc);
    setTimeout(() => replaceRef.current?.click(), 0);
  };

  const onReplaceFile = async (e) => {
    const f = e.target.files?.[0];
    e.target.value = "";
    if (!f || !replaceTarget) return;
    if (f.size > 50 * 1024 * 1024) { toast.error(`${f.name} exceeds the 50 MB limit`); return; }
    const note = window.prompt(`Replace "${replaceTarget.filename}" with "${f.name}"? Optional note for the version history:`, "") ?? null;
    if (note === null) { setReplaceTarget(null); return; }
    const fd = new FormData();
    fd.append("file", f);
    fd.append("note", note);
    try {
      const r = await axios.post(`${API}/api/documents/${replaceTarget.id}/replace`, fd, { headers });
      toast.success(`Replaced — now at v${r.data.version}`);
      fetchDocs();
    } catch (err) {
      toast.error(`Replace failed: ${err?.response?.data?.detail || err.message}`);
    } finally {
      setReplaceTarget(null);
    }
  };

  return (
    <div data-testid="documents-panel" style={{ padding: embedded ? 0 : 24 }}>
      {!embedded && <h2 style={{ fontSize: 22, fontWeight: 700, marginBottom: 16 }}>Documents</h2>}
      <UploadBar onUpload={onUpload} />
      <div style={{ display: "flex", gap: 8, marginTop: 12, marginBottom: 12, alignItems: "center" }}>
        <Filter size={14} style={{ color: "#6B7280" }} />
        <select data-testid="docs-category-filter" value={category} onChange={(e) => setCategory(e.target.value)}
          style={{ padding: "6px 8px", border: "1px solid #e5e7eb", borderRadius: 6, fontSize: 12 }}>
          <option value="">All categories</option>
          {CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
        </select>
        <input data-testid="docs-search" type="search" placeholder="Search filename, description, tag…"
          value={search} onChange={(e) => setSearch(e.target.value)}
          style={{ padding: "6px 10px", border: "1px solid #e5e7eb", borderRadius: 6, fontSize: 12, flex: 1, maxWidth: 320 }} />
        <span style={{ marginLeft: "auto", fontSize: 11, color: "#6B7280" }}>{docs.length} document{docs.length === 1 ? "" : "s"}</span>
      </div>
      {loading && <div style={{ padding: 16, color: "#6B7280" }}>Loading…</div>}
      {!loading && docs.length === 0 && (
        <div style={{ padding: 24, textAlign: "center", border: "1px dashed #e5e7eb", borderRadius: 8, color: "#6B7280", fontSize: 13 }}>
          No documents yet. Upload your first one above.
        </div>
      )}
      <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
        {docs.map(d => (
          <DocRow key={d.id} doc={d}
            onDownload={onDownload}
            onDelete={onDelete}
            onSign={() => setSignOpen(d)}
            onPreview={() => setPreviewOpen(d)}
            onReplace={() => onReplaceClick(d)}
            onVersions={() => setVersionsOpen(d)} />
        ))}
      </div>
      <input ref={replaceRef} type="file" style={{ display: "none" }} onChange={onReplaceFile} data-testid="docs-replace-input" />
      {signOpen && <SignatureModal doc={signOpen} onClose={() => { setSignOpen(null); fetchDocs(); }} />}
      {previewOpen && <PreviewModal doc={previewOpen} onClose={() => setPreviewOpen(null)} />}
      {versionsOpen && <VersionsModal doc={versionsOpen} onClose={() => setVersionsOpen(null)} />}
    </div>
  );
}

function UploadBar({ onUpload }) {
  const ref = useRef(null);
  const [cat, setCat] = useState("other");
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 8, padding: 12, border: "1px dashed #2563eb", borderRadius: 8, background: "rgba(37,99,235,0.04)" }}>
      <input ref={ref} type="file" multiple style={{ display: "none" }}
        data-testid="docs-upload-input"
        onChange={(e) => { onUpload(e.target.files, cat); e.target.value = ""; }} />
      <button onClick={() => ref.current?.click()} data-testid="docs-upload-btn"
        style={{ padding: "8px 14px", background: "#2563eb", color: "white", border: 0, borderRadius: 6, fontSize: 12, fontWeight: 600, cursor: "pointer", display: "inline-flex", alignItems: "center", gap: 6 }}>
        <Upload size={14} /> Upload
      </button>
      <select value={cat} onChange={(e) => setCat(e.target.value)} data-testid="docs-upload-category"
        style={{ padding: "6px 8px", border: "1px solid #e5e7eb", borderRadius: 6, fontSize: 12 }}>
        {CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
      </select>
      <span style={{ fontSize: 11, color: "#6B7280" }}>50 MB max · {COMPLIANCE.has(cat) ? "Will be stored in the secure compliance container." : "Stored in the standard container."}</span>
    </div>
  );
}

function DocRow({ doc, onDownload, onDelete, onSign, onPreview, onReplace, onVersions }) {
  const compliance = COMPLIANCE.has(doc.category);
  const version = doc.version || 1;
  return (
    <div data-testid={`doc-row-${doc.id}`} style={{ display: "flex", alignItems: "center", gap: 10, padding: "8px 12px", border: "1px solid #e5e7eb", borderRadius: 6, background: "white" }}>
      <FileText size={16} style={{ color: compliance ? "#dc2626" : "#2563eb" }} />
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
          <span style={{ fontSize: 13, fontWeight: 600, color: "#111827", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{doc.filename}</span>
          {compliance && <span style={{ fontSize: 9, padding: "1px 6px", background: "#fee2e2", color: "#7f1d1d", borderRadius: 3, fontWeight: 700, letterSpacing: 0.4 }}>COMPLIANCE</span>}
          {doc.signed && <span style={{ fontSize: 9, padding: "1px 6px", background: "#dcfce7", color: "#166534", borderRadius: 3, fontWeight: 700 }}>SIGNED · {doc.signatures?.length || 0}</span>}
          {version > 1 && (
            <button onClick={onVersions} data-testid={`doc-versions-${doc.id}`} title={`Version ${version} — view history`}
              style={{ fontSize: 9, padding: "1px 6px", background: "#eef2ff", color: "#3730a3", borderRadius: 3, fontWeight: 700, border: 0, cursor: "pointer" }}>
              v{version}
            </button>
          )}
        </div>
        <div style={{ fontSize: 11, color: "#6B7280", marginTop: 2 }}>
          {doc.category} · {fmt(doc.size)} · {doc.owner_name || doc.owner_email} · {new Date(doc.created_at).toLocaleDateString()}
          {doc.description ? ` · ${doc.description}` : ""}
        </div>
      </div>
      <button onClick={onPreview} data-testid={`doc-preview-${doc.id}`} title="Preview"
        style={{ padding: 6, border: 0, background: "transparent", cursor: "pointer", color: "#374151" }}><Eye size={14} /></button>
      <button onClick={() => onDownload(doc)} data-testid={`doc-download-${doc.id}`} title="Download"
        style={{ padding: 6, border: 0, background: "transparent", cursor: "pointer", color: "#374151" }}><Download size={14} /></button>
      <button onClick={onReplace} data-testid={`doc-replace-${doc.id}`} title="Replace with new version"
        style={{ padding: 6, border: 0, background: "transparent", cursor: "pointer", color: "#374151" }}><RefreshCw size={14} /></button>
      <button onClick={onVersions} data-testid={`doc-version-history-${doc.id}`} title="Version history"
        style={{ padding: 6, border: 0, background: "transparent", cursor: "pointer", color: "#374151" }}><History size={14} /></button>
      <button onClick={onSign} data-testid={`doc-sign-${doc.id}`} title="Sign"
        style={{ padding: 6, border: 0, background: "transparent", cursor: "pointer", color: "#374151" }}><PenTool size={14} /></button>
      <button onClick={() => onDelete(doc)} data-testid={`doc-delete-${doc.id}`} title="Delete"
        style={{ padding: 6, border: 0, background: "transparent", cursor: "pointer", color: "#dc2626" }}><Trash2 size={14} /></button>
    </div>
  );
}

function SignatureModal({ doc, onClose }) {
  const token = localStorage.getItem("token") || "";
  const canvasRef = useRef(null);
  const drawing = useRef(false);
  const lastPt = useRef(null);
  const [typedName, setTypedName] = useState("");
  const [title, setTitle] = useState("");
  const [agree, setAgree] = useState("");
  const [saving, setSaving] = useState(false);

  const start = (e) => { drawing.current = true; lastPt.current = ptFrom(e, canvasRef.current); };
  const move = (e) => {
    if (!drawing.current) return;
    const c = canvasRef.current; const ctx = c.getContext("2d");
    const pt = ptFrom(e, c);
    ctx.strokeStyle = "#111827"; ctx.lineWidth = 2; ctx.lineCap = "round";
    ctx.beginPath(); ctx.moveTo(lastPt.current.x, lastPt.current.y); ctx.lineTo(pt.x, pt.y); ctx.stroke();
    lastPt.current = pt;
  };
  const end = () => { drawing.current = false; lastPt.current = null; };
  const clear = () => { const c = canvasRef.current; c.getContext("2d").clearRect(0, 0, c.width, c.height); };

  const submit = async () => {
    if (!typedName.trim()) { toast.error("Type your full name"); return; }
    setSaving(true);
    try {
      const dataUrl = canvasRef.current.toDataURL("image/png");
      await axios.post(`${API}/api/documents/${doc.id}/sign`, {
        typed_name: typedName.trim(), title: title.trim(), agreement_text: agree.trim(),
        signature_data_url: dataUrl,
      }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success("Signed");
      onClose();
    } catch (e) {
      toast.error(`Sign failed: ${e?.response?.data?.detail || e.message}`);
    } finally { setSaving(false); }
  };

  return (
    <div data-testid="doc-sign-modal" style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.5)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1000 }}>
      <div style={{ background: "white", borderRadius: 12, width: 540, maxWidth: "95vw", padding: 24 }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
          <h3 style={{ margin: 0, fontSize: 16 }}>Sign &quot;{doc.filename}&quot;</h3>
          <button onClick={onClose} style={{ background: "transparent", border: 0, cursor: "pointer", color: "#6B7280" }}><X size={18} /></button>
        </div>
        <label style={{ display: "block", fontSize: 11, color: "#6B7280", marginBottom: 4 }}>Full legal name</label>
        <input value={typedName} onChange={(e) => setTypedName(e.target.value)} data-testid="sign-typed-name"
          style={{ width: "100%", padding: "7px 10px", border: "1px solid #e5e7eb", borderRadius: 6, marginBottom: 10, fontSize: 13 }} />
        <label style={{ display: "block", fontSize: 11, color: "#6B7280", marginBottom: 4 }}>Title (optional)</label>
        <input value={title} onChange={(e) => setTitle(e.target.value)} data-testid="sign-title"
          style={{ width: "100%", padding: "7px 10px", border: "1px solid #e5e7eb", borderRadius: 6, marginBottom: 10, fontSize: 13 }} />
        <label style={{ display: "block", fontSize: 11, color: "#6B7280", marginBottom: 4 }}>I agree (paste/edit terms — optional)</label>
        <textarea value={agree} onChange={(e) => setAgree(e.target.value)} rows={2} data-testid="sign-agreement"
          style={{ width: "100%", padding: "7px 10px", border: "1px solid #e5e7eb", borderRadius: 6, marginBottom: 10, fontSize: 12, fontFamily: "inherit" }} />
        <label style={{ display: "block", fontSize: 11, color: "#6B7280", marginBottom: 4 }}>Draw signature</label>
        <canvas ref={canvasRef} width={490} height={120} data-testid="sign-canvas"
          onMouseDown={start} onMouseMove={move} onMouseUp={end} onMouseLeave={end}
          onTouchStart={start} onTouchMove={move} onTouchEnd={end}
          style={{ border: "1px dashed #e5e7eb", borderRadius: 6, display: "block", marginBottom: 8, cursor: "crosshair", background: "white", touchAction: "none" }} />
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <button onClick={clear} style={{ padding: "5px 10px", border: "1px solid #e5e7eb", background: "white", borderRadius: 6, fontSize: 11, cursor: "pointer" }}>Clear</button>
          <button onClick={submit} disabled={saving} data-testid="sign-submit"
            style={{ padding: "8px 16px", background: "#2563eb", color: "white", border: 0, borderRadius: 6, fontSize: 12, fontWeight: 600, cursor: saving ? "default" : "pointer", opacity: saving ? 0.5 : 1 }}>
            {saving ? "Signing…" : "Sign document"}
          </button>
        </div>
      </div>
    </div>
  );
}

function ptFrom(e, canvas) {
  const r = canvas.getBoundingClientRect();
  const x = (e.touches?.[0]?.clientX ?? e.clientX) - r.left;
  const y = (e.touches?.[0]?.clientY ?? e.clientY) - r.top;
  return { x, y };
}

function PreviewModal({ doc, onClose }) {
  const token = localStorage.getItem("token") || "";
  const [data, setData] = useState(null);
  const [text, setText] = useState(null);
  const [err, setErr] = useState("");

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await axios.get(`${API}/api/documents/${doc.id}/preview`, { headers: { Authorization: `Bearer ${token}` } });
        if (cancelled) return;
        // Absolute (Azure) URL goes straight to the browser; relative
        // (local emulator) gets the API host prefixed.
        const signed = r.data.url || "";
        const url = /^https?:\/\//i.test(signed) ? signed : `${API}${signed}`;
        setData({ ...r.data, url });
        if (r.data.previewable && ((r.data.content_type || "").startsWith("text/") || r.data.content_type === "application/json" || r.data.content_type === "application/xml")) {
          try {
            // Backend-proxied to avoid Azure CORS — same-origin /api call.
            const tr = await axios.get(`${API}/api/documents/${doc.id}/preview/content`, {
              headers: { Authorization: `Bearer ${token}` },
              responseType: "text",
            });
            if (!cancelled) setText(tr.data || "");
          } catch (e2) {
            if (!cancelled) setErr("Couldn't fetch text body for preview.");
          }
        }
      } catch (e) {
        if (!cancelled) setErr(e?.response?.data?.detail || e.message || "Preview failed");
      }
    })();
    return () => { cancelled = true; };
  }, [doc.id, token]);

  const ct = data?.content_type || "";
  const isPdf = ct === "application/pdf";
  const isImage = ct.startsWith("image/");
  // text/plain, text/csv, text/html, text/markdown, text/xml + application/json + application/xml
  const isText = ct.startsWith("text/") || ct === "application/json" || ct === "application/xml";

  return (
    <div data-testid="doc-preview-modal" style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.65)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1000 }}>
      <div style={{ background: "white", borderRadius: 12, width: 920, maxWidth: "95vw", height: "85vh", display: "flex", flexDirection: "column", overflow: "hidden" }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "14px 18px", borderBottom: "1px solid #e5e7eb" }}>
          <div style={{ display: "flex", alignItems: "center", gap: 10, minWidth: 0 }}>
            <FileText size={16} style={{ color: "#2563eb", flexShrink: 0 }} />
            <span style={{ fontWeight: 600, fontSize: 14, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{doc.filename}</span>
            <span style={{ fontSize: 11, color: "#6B7280" }}>· {fmt(doc.size)} · {ct || doc.content_type || "—"}</span>
          </div>
          <button onClick={onClose} data-testid="doc-preview-close" style={{ background: "transparent", border: 0, cursor: "pointer", color: "#6B7280" }}><X size={20} /></button>
        </div>
        <div style={{ flex: 1, minHeight: 0, background: "#f9fafb", overflow: "auto" }}>
          {err && <div style={{ padding: 24, color: "#dc2626", fontSize: 13 }}>{err}</div>}
          {!err && !data && <div style={{ padding: 24, color: "#6B7280", fontSize: 13 }}>Loading preview…</div>}
          {data && !data.previewable && (
            <div style={{ padding: 32, textAlign: "center", color: "#6B7280", fontSize: 13 }}>
              This file type can&apos;t be previewed in-browser.{" "}
              <a href={data.url} target="_blank" rel="noopener noreferrer" download={doc.filename}
                style={{ color: "#2563eb", fontWeight: 600 }}>Download instead</a>
            </div>
          )}
          {data?.previewable && isPdf && (
            <iframe src={data.url} title={doc.filename} data-testid="doc-preview-pdf"
              style={{ border: 0, width: "100%", height: "100%", display: "block" }} />
          )}
          {data?.previewable && isImage && (
            <div style={{ display: "flex", alignItems: "center", justifyContent: "center", padding: 16, height: "100%" }}>
              <img src={data.url} alt={doc.filename} data-testid="doc-preview-image"
                style={{ maxWidth: "100%", maxHeight: "100%", objectFit: "contain", boxShadow: "0 4px 14px rgba(0,0,0,0.1)" }} />
            </div>
          )}
          {data?.previewable && isText && (
            <pre data-testid="doc-preview-text" style={{ margin: 0, padding: 18, fontSize: 12, fontFamily: "ui-monospace, Menlo, monospace", whiteSpace: "pre-wrap", wordBreak: "break-word", color: "#111827", background: "white", height: "100%", overflow: "auto" }}>
              {text == null ? "Loading content…" : text}
            </pre>
          )}
        </div>
        <div style={{ padding: "10px 18px", borderTop: "1px solid #e5e7eb", display: "flex", justifyContent: "flex-end", gap: 8 }}>
          {data?.url && (
            <a href={data.url} target="_blank" rel="noopener noreferrer" download={doc.filename}
              data-testid="doc-preview-download"
              style={{ padding: "7px 14px", background: "#2563eb", color: "white", border: 0, borderRadius: 6, fontSize: 12, fontWeight: 600, textDecoration: "none", display: "inline-flex", alignItems: "center", gap: 6 }}>
              <Download size={13} /> Download
            </a>
          )}
        </div>
      </div>
    </div>
  );
}

function VersionsModal({ doc, onClose }) {
  const token = localStorage.getItem("token") || "";
  const [list, setList] = useState(null);
  const [err, setErr] = useState("");
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await axios.get(`${API}/api/documents/${doc.id}/versions`, { headers: { Authorization: `Bearer ${token}` } });
        if (!cancelled) setList(r.data);
      } catch (e) {
        if (!cancelled) setErr(e?.response?.data?.detail || e.message || "Failed to load versions");
      }
    })();
    return () => { cancelled = true; };
  }, [doc.id, token]);

  return (
    <div data-testid="doc-versions-modal" style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.55)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1000 }}>
      <div style={{ background: "white", borderRadius: 12, width: 640, maxWidth: "95vw", maxHeight: "80vh", display: "flex", flexDirection: "column", overflow: "hidden" }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "14px 18px", borderBottom: "1px solid #e5e7eb" }}>
          <div>
            <div style={{ fontWeight: 700, fontSize: 14 }}>Version history</div>
            <div style={{ fontSize: 11, color: "#6B7280", marginTop: 2 }}>{doc.filename}</div>
          </div>
          <button onClick={onClose} data-testid="doc-versions-close" style={{ background: "transparent", border: 0, cursor: "pointer", color: "#6B7280" }}><X size={18} /></button>
        </div>
        <div style={{ padding: 14, overflow: "auto" }}>
          {err && <div style={{ color: "#dc2626", fontSize: 13 }}>{err}</div>}
          {!err && !list && <div style={{ color: "#6B7280", fontSize: 13 }}>Loading…</div>}
          {list?.versions?.map(v => {
            const href = v.url ? (/^https?:\/\//i.test(v.url) ? v.url : `${API}${v.url}`) : null;
            return (
              <div key={`${v.version}-${v.filename}`} data-testid={`doc-version-row-${v.version}`}
                style={{ display: "flex", alignItems: "center", gap: 10, padding: "10px 12px", border: "1px solid #e5e7eb", borderRadius: 6, marginBottom: 6, background: v.current ? "#f0f9ff" : "white" }}>
                <div style={{ width: 36, height: 36, borderRadius: 6, background: v.current ? "#2563eb" : "#e5e7eb", color: v.current ? "white" : "#374151", display: "flex", alignItems: "center", justifyContent: "center", fontWeight: 700, fontSize: 12, flexShrink: 0 }}>
                  v{v.version}
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 13, fontWeight: 600, color: "#111827", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                    {v.filename}
                    {v.current && <span style={{ marginLeft: 6, fontSize: 9, padding: "1px 6px", background: "#2563eb", color: "white", borderRadius: 3, fontWeight: 700 }}>CURRENT</span>}
                  </div>
                  <div style={{ fontSize: 11, color: "#6B7280", marginTop: 2 }}>
                    {fmt(v.size)} · {v.uploaded_at ? new Date(v.uploaded_at).toLocaleString() : "—"}
                  </div>
                  {v.note && (
                    <div style={{ fontSize: 11, color: "#374151", marginTop: 4, fontStyle: "italic", padding: "3px 8px", background: "#fef9c3", borderRadius: 4, display: "inline-block" }}
                      data-testid={`doc-version-note-${v.version}`}>
                      &ldquo;{v.note}&rdquo;
                    </div>
                  )}
                </div>
                {href && (
                  <a href={href} target="_blank" rel="noopener noreferrer" download={v.filename}
                    data-testid={`doc-version-download-${v.version}`}
                    style={{ padding: "5px 10px", background: "#f3f4f6", color: "#374151", border: 0, borderRadius: 6, fontSize: 11, fontWeight: 600, textDecoration: "none", display: "inline-flex", alignItems: "center", gap: 4 }}>
                    <Download size={12} /> Download
                  </a>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

export default function DocumentsPage() {
  return <DocumentsPanel />;
}
